import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { useAtomValue, useSetAtom } from 'jotai/react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Box as Container,
  Icon,
  Pagination,
  Sheet,
  SidePanel,
  Tabs,
  useSidePanel,
  type Tab,
} from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import { PageHeader } from 'components/header/PageHeader'
import { SidePanelHeaderTemplateTwo } from 'components/SidePanel/SidePanelHeaderTemplates/SidePanelHeaderTemplateTwo'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useBulkCapabiltiesQuery } from 'hooks/queries/Capabilities/useCapabiltiesQuery'
import { useCustomerQuery } from 'hooks/queries/Customers/useCustomerQuery'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { currentCustomerAtom, customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import { CustomerCard } from 'pages/Customers/CustomerCard/CustomerCard'
import { useCustomerCards } from 'pages/Customers/CustomerCard/hooks/useCustomerCards'
import { CustomerPaymentLimitsTable } from 'pages/Customers/CustomerView/CustomerPaymentLimitsTable'
import { CustomerWallets } from 'pages/Customers/CustomerView/CustomerWallets'
import {
  CustomerDetailTabs,
  CustomerModels,
  type CustomerDetailTab,
} from 'pages/Customers/helpers/types'
import { useCustomerWalletFilters } from 'pages/Customers/hooks/useCustomerWalletFilters'
import { AddWallet } from 'pages/NestedCustomers/components/AddWallet'
import CustomerInfo from 'pages/NestedCustomers/components/CustomerInfo'
import CustomerWalletSidepanelDetails from 'pages/NestedCustomers/components/CustomerWallet/CustomerWalletSidepanelDetails'
import { useCustomerWalletDepositDetails } from 'pages/NestedCustomers/hooks/useCustomerWalletDepositDetails'
import { useCustomerWalletsTableRowData } from 'pages/NestedCustomers/hooks/useCustomerWalletsTableRowData'
import { WalletTransactionExportModal } from 'pages/WalletDetails/components/RecentActivity/WalletTransactionsExport/WalletTransactionExportModal'
import {
  fiatSendFormAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from 'pages/Wallets/components/Pay/atoms/atoms'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { WalletActionsDialog } from 'pages/Wallets/components/WalletActionsDialog'
import { WalletHasFundsDialog } from 'pages/Wallets/components/WalletHasFundsDialog'
import { useWalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import {
  CustomerStatus,
  CustomerWallet,
  CustomerWalletDepositDetails,
  NestedCustomerDetails,
} from 'types/customers'
import { ActionIds, WalletTypes } from 'types/types'
import { WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import {
  WalletDialogTypes,
  WalletLifecycleActionTypes,
  type WalletDialogType,
  type WalletLifecycleAction,
} from 'types/wallets'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { getWalletActionCapabilityRequestFilters } from 'utils/capabilities'
import { highlightRowBasedOnId } from 'utils/table/table'
import track from 'utils/tracker'
import { roundNumberWithCommas } from 'utils/utils'
import { CustomerInfoSkeletonLoader } from './CustomerView/CustomerInfoSkeletonLoader'

const DIALOG_TYPE_TO_ACTION_MAP: Partial<Record<WalletDialogType, WalletLifecycleAction>> = {
  [WalletDialogTypes.BLOCK]: WalletLifecycleActionTypes.BLOCK,
  [WalletDialogTypes.UNBLOCK]: WalletLifecycleActionTypes.UNBLOCK,
  [WalletDialogTypes.TERMINATE]: WalletLifecycleActionTypes.TERMINATE,
}

export const Customer = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { enablePaymentLimitsManagement } = useFeatureFlags()
  const bannerSpacing = useComponentHeight(appBannersId)

  const { id: customerReference = '' }: { id?: string } = useParams()

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.CUSTOMER, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [customerReference])

  const { canTrade, canEdit } = useMappedRoles()?.wallets || {}
  const { cards } = useCustomerCards(customerReference)
  const hasAnyCards = (cards?.length ?? 0) > 0
  const showCardsTab = hasAnyCards
  const { viewOnly: isViewOnlyRole } = useProfileContext() || {}
  const [, setWalletAction] = useWalletActionContext()
  const { accountCapabilities } = useAccountsContext()
  const { isEpcEmbeddedCustomerWalletCreationCapabilityEnabled } = accountCapabilities || {}

  const setBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setFiatFormAtom = useSetAtom(fiatSendFormAtom)
  const setFiatBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setSourceWalletAtom = useSetAtom(selectedWalletAtom)
  const customerModel = useAtomValue(customerModelAtom)
  const setCurrentCustomerAtom = useSetAtom(currentCustomerAtom)

  const [activeTab, setActiveTab] = useState<CustomerDetailTab>(CustomerDetailTabs.WALLETS)
  const [isAddWalletSheetOpen, setIsAddWalletSheetOpen] = useState<boolean>(false)
  const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false)
  const [currentCustomerWallet, setCurrentCustomerWallet] = useState<CustomerWallet>()
  const [isTransactionExportModalVisible, setIsTransactionExportModalVisible] = useState(false)
  const [walletDialogState, setWalletDialogState] = useState<{
    wallet: CustomerWallet
    dialogType: WalletDialogType
  } | null>(null)

  const {
    filterProps,
    customerWallets,
    isFetchingCustomerWallets,
    isErrorFetchingCustomerWallets,
    fetchCustomerWallets,
    pagination,
  } = useCustomerWalletFilters(customerReference)

  const hasAnyWallets = (pagination?.paging?.total ?? 0) > 0

  const isEmbeddedOrRelianceCustomerModel =
    customerModel === CustomerModels.EMBEDDED || customerModel === CustomerModels.RELIANCE

  const {
    data: customer,
    isFetching: isFetchingCustomerInfo,
    isError: isErrorFetchingCustomerInfo,
    refetch: refetchCustomerInfo,
  } = useCustomerQuery<NestedCustomerDetails>(customerReference)

  const isFetchingWalletsData = useMemo(
    () => isFetchingCustomerWallets || isFetchingCustomerInfo,
    [isFetchingCustomerWallets, isFetchingCustomerInfo]
  )

  const fiatWalletCapabilitiesRequest = useMemo(() => {
    if (customerWallets.length) {
      const walletLsids = customerWallets.map(wallet => wallet?.id)

      return getWalletActionCapabilityRequestFilters(walletLsids)
    }
    return undefined
  }, [customerWallets])

  const {
    data: bulkCapabilities,
    isFetching: isFetchingCapabilities,
    isError: isErrorFetchingCapabilities,
  } = useBulkCapabiltiesQuery(fiatWalletCapabilitiesRequest, !!fiatWalletCapabilitiesRequest)

  useEffect(() => {
    setCurrentCustomerWallet(undefined)
  }, [])

  const { customerDetails, isFetchingCountries } = useCustomerWalletDepositDetails(
    customer as NestedCustomerDetails
  )

  useEffect(() => {
    if (customer) {
      setCurrentCustomerAtom(customer)
    }
    return () => {
      setCurrentCustomerAtom(undefined)
    }
  }, [customer, setCurrentCustomerAtom])

  const handleOnAddCustomerWallet = () => {
    setIsAddWalletSheetOpen(true)
  }

  const handleAddWalletSheetClose = () => {
    setIsAddWalletSheetOpen(false)
  }
  const {
    isSidePanelOpen: isCustomerWalletSidePanelOpen,
    sidePanelData: customerWalletData,
    paging: pagingCustomerWalletPaging,
    setIsSidePanelOpen: setIsCustomerWalletSidePanelOpen,
    updateSidePanel: updateCustomerWalletSidePanel,
  } = useSidePanel(customerWallets)

  const handleOnDetailsClick = (index: number) => {
    updateCustomerWalletSidePanel(index)
    setIsCustomerWalletSidePanelOpen(true)
  }

  const handleOpenFiatDepositSheet = (customerWallet: CustomerWallet) => {
    if (customerWallet && customer) {
      const customerWalletDepositDetails: CustomerWalletDepositDetails = {
        wallet: customerWallet,
        customer: customer,
      }
      setWalletAction({
        actionId: ActionIds?.FIAT_DEPOSIT,
        header: t('deposit'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        meta: {
          customerWalletDepositDetails,
        },
      })
      setIsSheetOpen(true)
    }
  }

  const handleOpenMoveSheet = (wallet: CustomerWallet) => {
    if (wallet) {
      setWalletAction({
        actionId: ActionIds.MOVE,
        header: t('customers.moveFunds'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        meta: {
          sourceWalletType: WalletTransferType?.CUSTOMER,
          sourceCustomerWallet: wallet,
          customerModel,
        },
      })
    }
    setIsSheetOpen(true)
  }

  const handleExportTransactionClick = (customerWallet: CustomerWallet) => {
    setCurrentCustomerWallet(customerWallet)
    setIsTransactionExportModalVisible(true)
  }

  const handleOnExportModalClose = () => {
    setIsTransactionExportModalVisible(false)
    setCurrentCustomerWallet(undefined)
  }

  const cleanupUnifiedPayoutsFlow = () => {
    setBeneficiaryAtom(null)
    setFiatFormAtom(null)
    setFiatBeneficiaryAtom(null)
    setSourceWalletAtom(null)
    setWalletAction(null)
    setIsSheetOpen(false)
  }

  const handleOpenSendFiatSheet = (customerWallet: CustomerWallet) => {
    if (customerWallet) {
      setWalletAction({
        actionId: ActionIds?.FIAT_SEND,
        header: t('wallets.fiatSend.beneficiaryStepHeader'),
        onClose: cleanupUnifiedPayoutsFlow,
        sourceWalletReference: customerWallet?.id,
        meta: {
          customerWallet: customerWallet,
        },
      })

      setIsSheetOpen(true)
    }
  }

  const handleOpenSendCryptoSheet = (customerWallet: CustomerWallet) => {
    if (customerWallet) {
      setWalletAction({
        actionId: ActionIds?.SEND,
        header: t('wallets.send.contactStepHeader'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        meta: {
          customerWallet: customerWallet,
        },
      })
      setIsSheetOpen(true)
    }
  }

  const handleWalletStatusUpdate = (wallet: CustomerWallet, action: WalletLifecycleAction) => {
    switch (action) {
      case WalletLifecycleActionTypes.BLOCK:
        setWalletDialogState({ wallet, dialogType: WalletDialogTypes.BLOCK })
        break
      case WalletLifecycleActionTypes.UNBLOCK:
        setWalletDialogState({ wallet, dialogType: WalletDialogTypes.UNBLOCK })
        break
      case WalletLifecycleActionTypes.TERMINATE: {
        const hasFunds = Number(wallet?.balance?.value) > 0
        setWalletDialogState({
          wallet,
          dialogType: hasFunds ? WalletDialogTypes.HAS_FUNDS : WalletDialogTypes.TERMINATE,
        })
        break
      }
    }
  }

  const handleOpenWalletNameEditSheet = (wallet: CustomerWallet) => {
    setWalletAction({
      actionId: ActionIds?.WALLET_EDIT,
      header: t('wallets.editWalletName'),
      onClose: () => {
        setIsSheetOpen(false)
        queryClient.invalidateQueries({ queryKey: ['customerWallets'] })
      },
      meta: {
        customerWallet: wallet,
      },
    })
    setIsSheetOpen(true)
  }

  const closeWalletDialog = () => setWalletDialogState(null)

  const walletActionForDialog = walletDialogState?.dialogType
    ? DIALOG_TYPE_TO_ACTION_MAP[walletDialogState.dialogType]
    : undefined

  const isActiveCustomer = customer?.status === CustomerStatus.VERIFIED
  const walletTableRows = useCustomerWalletsTableRowData({
    bulkCapabilities,
    isFetchingCapabilities,
    customerWallets,
    isActiveCustomer,
    handleExportTransactionClick,
    handleOnDetailsClick,
    handleOpenMoveSheet,
    handleOpenFiatDepositSheet,
    handleOpenSendFiatSheet,
    handleOpenSendCryptoSheet,
    handleWalletStatusUpdate,
    handleOpenWalletNameEditSheet,
  })

  const onReloadWalletsData = useCallback(() => {
    isErrorFetchingCustomerWallets && fetchCustomerWallets()
  }, [isErrorFetchingCustomerWallets, fetchCustomerWallets])

  const isAddWalletEnabled = useMemo(() => {
    if ((!canEdit && !canTrade) || !isActiveCustomer) return false

    if (isEmbeddedOrRelianceCustomerModel) {
      return isEpcEmbeddedCustomerWalletCreationCapabilityEnabled
    }

    return true
  }, [
    canEdit,
    isEmbeddedOrRelianceCustomerModel,
    isActiveCustomer,
    canTrade,
    isEpcEmbeddedCustomerWalletCreationCapabilityEnabled,
  ])

  const AddWalletButton = isAddWalletEnabled ? (
    <Button
      iconElement={<Icon name="Plus" />}
      disabled={isViewOnlyRole}
      onClick={handleOnAddCustomerWallet}
    >
      {t('customers.addWallet')}
    </Button>
  ) : undefined

  const CustomerWalletsView = useMemo(() => {
    if (isErrorFetchingCustomerWallets || isErrorFetchingCapabilities) {
      return <RequestErrorStatusCard onReload={onReloadWalletsData} />
    }
  }, [isErrorFetchingCustomerWallets, isErrorFetchingCapabilities, onReloadWalletsData])

  const CustomerInfoView = useMemo(() => {
    if (isFetchingCustomerInfo || isFetchingCountries) {
      return <CustomerInfoSkeletonLoader />
    }
    if (customerDetails) {
      return <CustomerInfo customerDetails={customerDetails} />
    }
  }, [isFetchingCustomerInfo, isFetchingCountries, customerDetails])

  const customerTabs: Tab[] = [
    {
      id: CustomerDetailTabs.WALLETS,
      header: t('customers.tabs.wallets'),
      content: null,
      testId: 'customer-wallets-tab',
    },
    ...(showCardsTab
      ? [
          {
            id: CustomerDetailTabs.CARDS,
            header: t('customers.tabs.cards'),
            content: null,
            testId: 'customer-cards-tab',
          },
        ]
      : []),
    {
      id: CustomerDetailTabs.CUSTOMER_DETAILS,
      header: t('customers.tabs.customerDetails'),
      content: null,
      testId: 'customer-details-tab',
    },
    ...(enablePaymentLimitsManagement
      ? [
          {
            id: CustomerDetailTabs.PAYMENT_LIMITS,
            header: t('customers.tabs.paymentLimits'),
            content: null,
            testId: 'customer-payment-limits-tab',
          },
        ]
      : []),
  ]

  const iconUrl = customerWalletData?.balance?.currencyCode
    ? `${process.env.ICONS_URL}${customerWalletData.balance.currencyCode.toLowerCase()}.svg`
    : undefined

  const iconUrls = iconUrl ? [iconUrl] : []

  return (
    <>
      <PageHeader
        description={
          isEmbeddedOrRelianceCustomerModel
            ? t('embeddedCustomers.details.description')
            : t('customers.customerDetailsDescription')
        }
        {...(activeTab === CustomerDetailTabs.WALLETS &&
          hasAnyWallets && {
            renderRightSection: AddWalletButton,
          })}
      />
      {isErrorFetchingCustomerInfo ? (
        <RequestErrorStatusCard onReload={refetchCustomerInfo} />
      ) : (
        <>
          <Box paddingB={16}>
            <Tabs
              tabs={customerTabs}
              defaultTabId={activeTab}
              onChange={tabId => setActiveTab(tabId as CustomerDetailTab)}
              testId="customer-detail-tabs"
            />
          </Box>
          {activeTab === CustomerDetailTabs.CUSTOMER_DETAILS && CustomerInfoView}
          {activeTab === CustomerDetailTabs.WALLETS && (
            <>
              {CustomerWalletsView ?? (
                <>
                  <CustomerWallets
                    filterProps={filterProps}
                    isCustomerVerified={isActiveCustomer}
                    customerName={customerDetails?.name ?? ''}
                    isLoading={isFetchingWalletsData || isFetchingCapabilities}
                    walletTableRows={walletTableRows}
                    AddWalletButton={AddWalletButton}
                  />
                  {pagination.isVisible && (
                    <Box
                      className={clsx({
                        'opacity-25': isFetchingCustomerWallets,
                      })}
                    >
                      <Pagination
                        testId="customer-wallets-pagination"
                        page={pagination.page}
                        rowsPerPage={pagination.rowsPerPage}
                        total={pagination.paging?.total ?? 0}
                        onChangePage={pagination.onChangePage}
                        onChangeRowsPerPage={pagination.onChangeRowsPerPage}
                      />
                    </Box>
                  )}
                </>
              )}
            </>
          )}
          {activeTab === CustomerDetailTabs.CARDS && <CustomerCard />}
          {activeTab === CustomerDetailTabs.PAYMENT_LIMITS && customerReference && (
            <CustomerPaymentLimitsTable customerReference={customerReference} />
          )}
        </>
      )}

      {customerWalletData && (
        <SidePanel
          id={'side-panel-customer-wallet'}
          isOpen={isCustomerWalletSidePanelOpen}
          onClose={() => setIsCustomerWalletSidePanelOpen(false)}
          paging={pagingCustomerWalletPaging}
          bodyScrollable={false}
          activeRowId={customerWalletData.id}
          onActiveRowChange={highlightRowBasedOnId}
          topOffset={bannerSpacing}
          header={
            <SidePanelHeaderTemplateTwo
              heading={{
                iconUrls,
                primaryLabel: customerWalletData.name,
                secondaryLabel: `${roundNumberWithCommas(Number(customerWalletData.balance?.value) || 0, 2)} ${customerWalletData.balance?.currencyCode}`,
              }}
            />
          }
          body={
            <Container flex flexCol gapY={12} width="full">
              <CustomerWalletSidepanelDetails customerWalletData={customerWalletData} />
            </Container>
          }
        />
      )}

      <Sheet
        isOpen={isAddWalletSheetOpen}
        header={t('customers.newWallet')}
        onClose={handleAddWalletSheetClose}
      >
        <AddWallet walletType={WalletTypes?.CUSTOMER} handleClose={handleAddWalletSheetClose} />
      </Sheet>

      <WalletSheet isSheetOpen={isSheetOpen} />

      {isTransactionExportModalVisible && (
        <WalletTransactionExportModal
          key={currentCustomerWallet?.id}
          handleClose={handleOnExportModalClose}
          downloadAll={false}
          customerWallet={currentCustomerWallet}
        />
      )}

      {walletActionForDialog && walletDialogState && (
        <WalletActionsDialog
          walletId={walletDialogState.wallet.id}
          walletName={walletDialogState.wallet.name}
          action={walletActionForDialog}
          onClose={closeWalletDialog}
          invalidateQueryKeys={[[reactQueryName.CUSTOMER_WALLETS]]}
        />
      )}

      {walletDialogState?.dialogType === WalletDialogTypes.HAS_FUNDS && (
        <WalletHasFundsDialog
          walletName={walletDialogState.wallet.name}
          onClose={closeWalletDialog}
          onMoveFunds={() => {
            handleOpenMoveSheet(walletDialogState.wallet)
            closeWalletDialog()
          }}
        />
      )}
    </>
  )
}
